import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Schedule } from "./components/Schedule";
import { StudentsView } from "./components/StudentsView";
import { TimeslotManager } from "./components/TimeslotManager";
import { SchoolManager } from "./components/SchoolManager";
import {
  studentsApi,
  timeslotsApi,
  assignmentsApi,
  preferencesApi,
  schoolsApi,
  authApi,
  AssignmentDTO,
} from "./api/client";
import type {
  Student,
  Timeslot,
  ScheduleAssignment,
  TimeslotPreference,
  School,
} from "./types";
import "./App.css";

function App() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState("schools");
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [assignments, setAssignments] = useState<ScheduleAssignment[]>([]);
  const [preferences, setPreferences] = useState<TimeslotPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<{
    id: number;
    name: string;
    email: string;
  } | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved as "light" | "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Try fetch current user session
    const loadUser = async () => {
      try {
        const res = await authApi.me();
        setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (user) {
      loadSchools();
    }
  }, [user]);

  useEffect(() => {
    if (selectedSchool) {
      loadData();
    }
  }, [selectedSchool]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const loadSchools = async () => {
    try {
      const response = await schoolsApi.getAll();
      setSchools(response.data);
      if (response.data.length > 0) {
        const savedIdStr = localStorage.getItem("selectedSchoolId");
        const savedId = savedIdStr ? Number(savedIdStr) : undefined;
        if (savedId && response.data.some((s) => s.id === savedId)) {
          const found = response.data.find((s) => s.id === savedId) || null;
          setSelectedSchool(found);
        } else if (!selectedSchool) {
          setSelectedSchool(response.data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to load schools:", error);
    }
  };

  const loadData = async () => {
    if (!selectedSchool) return;

    setLoading(true);
    try {
      const [studentsRes, timeslotsRes, assignmentsRes] = await Promise.all([
        studentsApi.getBySchool(selectedSchool.id),
        timeslotsApi.getBySchool(selectedSchool.id),
        assignmentsApi.getBySchool(selectedSchool.id),
      ]);
      setStudents(studentsRes.data);
      setTimeslots(timeslotsRes.data);

      const allPreferences: TimeslotPreference[] = [];
      for (const student of studentsRes.data) {
        try {
          const prefRes = await preferencesApi.getByStudent(student.id);
          allPreferences.push(...prefRes.data);
        } catch (error) {
          console.error(
            `Failed to load preferences for student ${student.id}:`,
            error,
          );
        }
      }
      setPreferences(allPreferences);

      const assignmentsWithNames = assignmentsRes.data.map(
        (a: AssignmentDTO) => {
          const student = studentsRes.data.find((s) => s.id === a.student_id);
          const timeslot = timeslotsRes.data.find(
            (t) => t.id === a.timeslot_id,
          );
          return {
            student_id: a.student_id,
            student_name: student?.name || "Unknown",
            timeslot_id: a.timeslot_id,
            day: timeslot?.day || "monday",
            start_time: timeslot?.start_time || "00:00",
          };
        },
      );
      setAssignments(assignmentsWithNames);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "de" : "en");
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const handleSchoolSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const school = schools.find((s) => s.id === id) || null;
    setSelectedSchool(school);
    try {
      localStorage.setItem("selectedSchoolId", String(id));
    } catch {}
  };

  const handleLogout = async () => {
    await authApi.logout();
    setUser(null);
    setUserMenuOpen(false);
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="app">
        <div className="login-page">
          <div className="login-header">
            <h1>{t("app.title")}</h1>
            <p>{t("app.subtitle")}</p>
          </div>
          <div className="login-content">
            <div className="login-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return (
      <div className="app">
        <div className="login-page">
          <header className="login-header">
            <div className="login-header-content">
              <h1>{t("app.title")}</h1>
              <p>{t("app.subtitle")}</p>
            </div>
            <div className="login-header-actions">
              <button
                onClick={toggleTheme}
                className="theme-toggle"
                title={t("common.toggleTheme")}
              >
                {theme === "light" ? "🌙" : "☀️"}
              </button>
              <button onClick={toggleLanguage} className="language-toggle">
                {i18n.language === "en" ? "Deutsch" : "English"}
              </button>
            </div>
          </header>
          <div className="login-content">
            <div className="login-card">
              <div className="login-icon">🎹</div>
              <h2>{t("login.welcome")}</h2>
              <p>{t("login.description")}</p>
              <button className="login-button" onClick={() => authApi.login()}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t("login.signInWithGoogle")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main app (authenticated)
  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <h1>{t("app.title")}</h1>
          <p>{t("app.subtitle")}</p>
        </div>
        <div className="header-actions">
          {schools.length > 0 && (
            <select
              className="school-select"
              value={selectedSchool?.id ?? ""}
              onChange={handleSchoolSelect}
              title={t("schools.selectSchool")}
            >
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={toggleTheme}
            className="theme-toggle"
            title={t("common.toggleTheme")}
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
          <button onClick={toggleLanguage} className="language-toggle">
            {i18n.language === "en" ? "Deutsch" : "English"}
          </button>

          {/* User menu */}
          <div className="user-menu-container" ref={userMenuRef}>
            <button
              className="user-menu-button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <span className="user-avatar">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </span>
              <span className="user-name">{user.name || user.email}</span>
              <svg
                className={`user-menu-arrow ${userMenuOpen ? "open" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
              >
                <path
                  fill="currentColor"
                  d="M2.5 4.5L6 8l3.5-3.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
            </button>
            {userMenuOpen && (
              <div className="user-menu-dropdown">
                <div className="user-menu-header">
                  <span className="user-menu-email">{user.email}</span>
                </div>
                <button className="user-menu-item" onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("common.logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="nav">
        <button
          className={view === "schools" ? "nav-btn active" : "nav-btn"}
          onClick={() => setView("schools")}
        >
          {t("nav.schools")}
        </button>
        <button
          className={view === "students" ? "nav-btn active" : "nav-btn"}
          onClick={() => setView("students")}
          disabled={!selectedSchool}
        >
          {t("nav.students")}
        </button>
        <button
          className={view === "timeslots" ? "nav-btn active" : "nav-btn"}
          onClick={() => setView("timeslots")}
          disabled={!selectedSchool}
        >
          {t("timeslots.title")}
        </button>
        <button
          className={view === "schedule" ? "nav-btn active" : "nav-btn"}
          onClick={() => setView("schedule")}
          disabled={!selectedSchool}
        >
          {t("nav.schedule")}
        </button>
      </nav>

      <main className="main">
        {view === "schools" && (
          <div className="view">
            <SchoolManager onSchoolsChange={loadSchools} canEdit={!!user} />
          </div>
        )}

        {view === "students" && selectedSchool && (
          <div className="view">
            <StudentsView
              students={students}
              schools={schools}
              schoolId={selectedSchool.id}
              onStudentsChange={loadData}
              canEdit={!!user}
            />
          </div>
        )}

        {view === "timeslots" && selectedSchool && (
          <div className="view">
            <TimeslotManager
              timeslots={timeslots}
              schoolId={selectedSchool.id}
              onTimeslotsChange={loadData}
              canEdit={!!user}
            />
          </div>
        )}

        {view === "schedule" && selectedSchool && (
          <div className="view">
            <h2>
              {t("schedule.title")} - {selectedSchool.name}
            </h2>
            {loading ? (
              <div
                className="card"
                style={{ textAlign: "center", padding: "3rem" }}
              >
                <p>{t("common.loading")}</p>
              </div>
            ) : (
              <Schedule
                students={students}
                timeslots={timeslots}
                assignments={assignments}
                preferences={preferences}
                onAssignmentsChange={setAssignments}
                schoolId={selectedSchool.id}
              />
            )}
          </div>
        )}

        {!selectedSchool && view !== "schools" && (
          <div className="view">
            <div
              className="card"
              style={{ textAlign: "center", padding: "3rem" }}
            >
              <h2>{t("schools.selectSchool")}</h2>
              <p>{t("schools.selectSchoolPrompt")}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
